import { useEffect, useMemo, useState } from "react";
import { WORLDS, taskCount } from "@/flow-content/curriculum";
import { useLearnProgress } from "./useLearnProgress";
import { useLearnerModel } from "./useLearnerModel";

/**
 * ModulePlayer — "DIE ARENA"  ///  AAA rebuild
 * ─────────────────────────────────────────────────────────────────────────────
 * Playing a module is now a fullscreen takeover (fixed inset-0), not a page:
 *   · the whole arena breathes in the world's colour (aura + floating orbs)
 *   · a cinematic 1s title card ("WORTWALD — Buchstaben-Schmiede") before play
 *   · a floating glass HUD (back · title · XP bounty)
 *   · the game sits vertically centred on every screen — no cut-offs,
 *     no dead widescreen space
 *   · a celebratory result stage: confetti, counting XP, popping stars
 * All learning logic (adaptive plan, ELO, spaced repetition) untouched.
 */

/* Adapt a module's task set to the learner: struggling → a shorter, focused set;
   otherwise the full challenge. Keeps the module shape intact for the game modes. */
function adaptModule(module, plan) {
  if (!plan?.struggling) return module;
  const trim = (arr) => (arr ? arr.slice(0, Math.max(4, Math.ceil(arr.length * 0.7))) : arr);
  return { ...module, questions: trim(module.questions), items: trim(module.items), pairs: trim(module.pairs) };
}

import QuizDuell from "./games/QuizDuell";
import Zuordnung from "./games/Zuordnung";
import Lueckentext from "./games/Lueckentext";
import Tempo from "./games/Tempo";
import Memory from "./games/Memory";

const GAMES = { quiz: QuizDuell, match: Zuordnung, cloze: Lueckentext, tempo: Tempo, memory: Memory };
const MODE_LABEL = { quiz: "Quiz-Duell", match: "Zuordnung", cloze: "Lückentext", tempo: "Zeit-Challenge", memory: "Memory" };
const MODE_ICON  = { quiz: "❓", match: "🔗", cloze: "✏️", tempo: "⏱️", memory: "🃏" };

export default function ModulePlayer({ module, onExit }) {
  const world = WORLDS.find((w) => w.id === module.world);
  const color = world?.color || "#8b5cf6";
  const { recordResult } = useLearnProgress();
  const learner = useLearnerModel();
  const [phase, setPhase] = useState("intro");     // intro | play | result
  const [runId, setRunId] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const Game = GAMES[module.mode] || QuizDuell;

  const played = useMemo(() => adaptModule(module, learner.planFor(module)), [module.id, runId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* cinematic title card → play */
  useEffect(() => {
    if (phase !== "intro") return;
    const t = setTimeout(() => setPhase("play"), 1400);
    return () => clearTimeout(t);
  }, [phase]);

  const finish = async ({ correct, total }) => {
    setBusy(true);
    const r = await recordResult({ moduleId: module.id, correct, total, xp: module.xp, coins: module.coins });
    learner.recordOutcome(module, correct, total);
    setResult({ correct, total, ...r });
    setPhase("result");
    setBusy(false);
  };

  const replay = () => { setResult(null); setRunId((n) => n + 1); setPhase("intro"); };

  return (
    <div className="fixed inset-0 z-[56] flex flex-col overflow-hidden text-white"
      style={{ background: `radial-gradient(1100px 600px at 50% -10%, ${color}26, transparent 60%), radial-gradient(800px 500px at 12% 110%, ${color}14, transparent 55%), #05041a` }}>
      <style>{`
        @keyframes arena-orb   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-26px); } }
        @keyframes arena-in    { from { opacity: 0; transform: translateY(16px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes arena-title { 0% { opacity: 0; transform: scale(1.4); filter: blur(10px); } 25% { opacity: 1; transform: scale(1); filter: blur(0); } 80% { opacity: 1; } 100% { opacity: 0; transform: scale(0.96); } }
        @keyframes arena-sub   { 0%,30% { opacity: 0; transform: translateY(8px); } 45% { opacity: 1; transform: translateY(0); } 85% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes sx-pop      { 0% { transform: scale(0) rotate(-30deg); opacity: 0; } 70% { transform: scale(1.35); } 100% { transform: scale(1.15); opacity: 1; } }
        @keyframes sx-confetti { 0% { opacity: 1; transform: translate(0,0) rotate(0); } 100% { opacity: 0; transform: translate(var(--cx), var(--cy)) rotate(var(--cr)); } }
        @keyframes sx-shake    { 0%,100% { transform: translateX(0); } 20% { transform: translateX(-7px); } 40% { transform: translateX(7px); } 60% { transform: translateX(-5px); } 80% { transform: translateX(5px); } }
      `}</style>

      {/* floating ambient orbs in the world colour */}
      {[{ l: "6%", t: "18%", s: 180, d: "7s" }, { l: "86%", t: "12%", s: 120, d: "9s" }, { l: "78%", t: "72%", s: 220, d: "11s" }].map((o, i) => (
        <div key={i} className="pointer-events-none absolute rounded-full blur-3xl"
          style={{ left: o.l, top: o.t, width: o.s, height: o.s, background: `${color}1c`, animation: `arena-orb ${o.d} ease-in-out infinite` }} />
      ))}

      {/* ═══ HUD — floating glass pill ═══════════════════════════════════ */}
      <div className="relative z-10 mx-auto mt-4 flex w-full max-w-4xl items-center gap-3 px-4">
        <div className="flex w-full items-center gap-3 rounded-2xl border border-white/10 px-3 py-2.5"
          style={{ background: "rgba(10,8,30,0.72)", backdropFilter: "blur(14px)", boxShadow: `0 8px 30px rgba(0,0,0,0.4), 0 0 0 1px ${color}22` }}>
          <button onClick={onExit} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-white/12 bg-white/5 text-white/80 transition-all hover:scale-105 hover:bg-white/10">←</button>
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-lg" style={{ background: `${color}1e`, border: `1px solid ${color}45` }}>{world?.emoji}</span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-black">{module.title}</p>
            <p className="truncate text-[10px] font-bold uppercase tracking-wider text-white/45">{MODE_ICON[module.mode]} {MODE_LABEL[module.mode]} · {taskCount(module)} Aufgaben</p>
          </div>
          <span className="shrink-0 rounded-full px-3 py-1.5 text-xs font-black" style={{ background: "rgba(251,191,36,0.14)", color: "#fbbf24", border: "1px solid rgba(251,191,36,0.35)" }}>+{module.xp} XP</span>
        </div>
      </div>

      {/* ═══ STAGE — vertically centred on every screen ══════════════════ */}
      <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-4 py-6">
        {phase === "intro" && (
          <div className="text-center">
            <p className="text-xs font-black uppercase tracking-[0.4em]" style={{ color, animation: "arena-sub 1.4s ease both" }}>{world?.emoji} {world?.name}</p>
            <h1 className="mt-2 text-4xl font-black sm:text-6xl" style={{ animation: "arena-title 1.4s ease both", textShadow: `0 0 60px ${color}80` }}>{module.title}</h1>
            <p className="mt-3 text-sm font-bold text-white/50" style={{ animation: "arena-sub 1.4s ease both" }}>{MODE_LABEL[module.mode]} · Mach dich bereit …</p>
          </div>
        )}
        {phase === "play" && (
          <div className="w-full" style={{ animation: "arena-in 0.4s ease both" }}>
            <Game key={runId} module={played} accent={color} onFinish={finish} />
          </div>
        )}
        {phase === "result" && result && (
          <Result module={module} world={world} result={result} busy={busy} onReplay={replay} onExit={onExit} />
        )}
      </div>
    </div>
  );
}

/* ═══ RESULT — celebration stage ═════════════════════════════════════════ */
const CONFETTI_COLORS = ["#8b5cf6", "#22d3ee", "#fbbf24", "#ec4899", "#22c55e", "#ffffff"];

function Result({ module, world, result, onReplay, onExit }) {
  const { correct, total, stars, awarded, streak, firstClear } = result;
  const color = world?.color || "#8b5cf6";
  const pct = Math.round((correct / Math.max(1, total)) * 100);
  const perfect = stars >= 3;
  const [xpShown, setXpShown] = useState(0);

  /* counting XP number */
  useEffect(() => {
    const target = awarded?.xp ?? 0;
    if (!target) return;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 900);
      setXpShown(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [awarded?.xp]);

  const confetti = useMemo(() => Array.from({ length: pct >= 50 ? 42 : 14 }, (_, i) => ({
    cx: `${(Math.random() - 0.5) * 480}px`,
    cy: `${140 + Math.random() * 360}px`,
    cr: `${(Math.random() - 0.5) * 720}deg`,
    c: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    d: 1 + Math.random() * 0.9,
    dl: Math.random() * 0.35,
    s: 5 + Math.random() * 6,
  })), [pct]);

  return (
    <div className="relative mx-auto w-full max-w-md text-center" style={{ animation: "arena-in 0.4s ease both" }}>
      {/* confetti rain */}
      <div className="pointer-events-none absolute inset-x-0 -top-10 flex justify-center">
        {confetti.map((p, i) => (
          <div key={i} className="absolute rounded-sm" style={{
            width: p.s, height: p.s * 0.6, background: p.c,
            "--cx": p.cx, "--cy": p.cy, "--cr": p.cr,
            animation: `sx-confetti ${p.d}s cubic-bezier(0.2,0.6,0.4,1) ${p.dl}s both`,
          }} />
        ))}
      </div>

      {/* medal */}
      <div className="relative mx-auto mb-3 h-28 w-28">
        <div className="absolute inset-0 animate-ping rounded-full" style={{ background: `${color}30` }} />
        <div className="absolute -inset-3 rounded-full" style={{ border: `2px solid ${color}40` }} />
        <div className="relative grid h-28 w-28 place-items-center rounded-full text-6xl"
          style={{ background: `linear-gradient(135deg, ${color}, #ec4899)`, boxShadow: `0 0 50px ${color}70, 0 16px 40px rgba(0,0,0,0.5)` }}>
          {perfect ? "🏆" : pct >= 50 ? "🎉" : "💪"}
        </div>
      </div>

      <h2 className="text-3xl font-black">{perfect ? "PERFEKT!" : pct >= 50 ? "Stark gemacht!" : "Weiter üben!"}</h2>
      <p className="mt-1 text-sm font-bold text-white/55">{correct} von {total} richtig · {pct}%</p>

      {/* stars */}
      <div className="mt-4 flex items-center justify-center gap-2">
        {[1, 2, 3].map((s) => (
          <span key={s} className="text-5xl" style={{
            transform: `scale(${s <= stars ? 1.15 : 0.8})`,
            filter: s <= stars ? `drop-shadow(0 0 14px #fbbf24)` : "grayscale(1) opacity(0.3)",
            animation: s <= stars ? `sx-pop 0.45s ${0.2 + s * 0.15}s both` : "none",
          }}>⭐</span>
        ))}
      </div>

      {/* rewards */}
      <div className="mt-6 grid grid-cols-3 gap-2.5">
        <Stat label="XP" value={`+${xpShown}`} c="#8b5cf6" big />
        <Stat label="Coins" value={`+${awarded?.coins ?? 0}`} c="#fbbf24" />
        <Stat label="Streak" value={`🔥 ${streak}`} c="#f97316" />
      </div>

      {firstClear && (
        <div className="mt-4 rounded-2xl border border-gold/40 bg-gold/10 px-4 py-3 text-sm font-black text-gold" style={{ animation: "sx-pop 0.5s 0.6s both" }}>
          🏅 Neues Abzeichen: {module.badge}
        </div>
      )}

      <div className="mt-7 grid gap-2.5">
        <button onClick={onExit}
          className="rounded-2xl px-6 py-4 text-base font-black text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: `linear-gradient(135deg, ${color}, #ec4899)`, boxShadow: `0 0 34px ${color}50, 0 12px 28px rgba(0,0,0,0.45)` }}>
          Zurück zur Galaxie →
        </button>
        <button onClick={onReplay} className="rounded-2xl border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-bold text-white/85 transition-colors hover:bg-white/10">
          ↻ Nochmal spielen
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, c, big }) {
  return (
    <div className="rounded-2xl border border-white/10 py-3" style={{ background: `${c}0c` }}>
      <div className={`${big ? "text-2xl" : "text-lg"} font-black tabular-nums`} style={{ color: c, textShadow: `0 0 16px ${c}60` }}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-white/45">{label}</div>
    </div>
  );
}
